import bcrypt from "bcryptjs";
import { mutation, query } from "gql-query-builder";
import differenceInHours from "date-fns/differenceInHours";
import { buildVariables } from "../libs/GraphQueryBuilder";
import {
  CreatePollInput,
  CreatePollMutationVariables,
  CreatePollReportInput,
  CreatePollReportMutationVariables,
  CreateSubPollInput,
  CreateSubPollMutationVariables,
  DeletePollMutationVariables,
  GetPollQueryVariables,
  ListPublicPollsQueryVariables,
  ListPollsByOwnerQueryVariables,
  ListReportsByPollQueryVariables,
  ListSubPollsByMainPollQueryVariables,
  ModelSortDirection,
  PollOptionInput,
  PollsByStatusQueryVariables,
  PollStatus,
  S3ObjectInput,
  SubPoll,
  UpdatePollMutationVariables,
  UpdateSubPollMutationVariables,
  Tier,
  ListCustomConfigurationByCompanyQueryVariables,
  GetPollCountQueryVariables,
  CanCreatePremiumPollInput,
  CanCreatePremiumPollMutationVariables,
  CreatePollCommentInput,
  CreatePollCommentMutationVariables,
} from "../API";
import API from "@aws-amplify/api";
import { getCompanyUserByUser, getUser } from "./UsersManager";
const DEFAULT_POLL_CAP = 1000;

export function hashPollPassword(password: string) {
  let salt = bcrypt.genSaltSync(10);
  let hash = bcrypt.hashSync(password, salt);
  return hash;
}

export function compareHashPollPassword(hash: string, password: string) {
  return bcrypt.compareSync(password, hash);
}

export function setPollPassword(pollID: string, password, username) {
  return editPoll(
    ["id"],
    pollID,
    { input: { id: pollID, password: hashPollPassword(password) } },
    username
  );
}

async function comparePollPassword(pollID, password) {
  const q: any = getPollByIDQuery(["password"], { id: pollID });
  const poll: any = await API.graphql(q);
  const hashPassword = poll.data.result.password;
  if (hashPassword === undefined || hashPassword === null) {
    return true;
  } else {
    if (password === "") {
      throw new Error("missing password for the poll");
    }
  }
  return bcrypt.compareSync(password, hashPassword);
}

async function getAdminUserByUser(userId: string) {
  const compUser: any = await getCompanyUserByUser(userId);
  const adminRole = compUser.data?.result?.items?.[0];
  const admin: any = await getUser(["username"], adminRole.adminID);
  return {
    ...adminRole,
    ...admin.data?.result,
  };
}

export async function listCompanyPolls(
  fields: any[],
  userID: string,
  { nextToken, ...options }: Partial<ListPollsByOwnerQueryVariables> = {}
) {
  const admin: any = await getAdminUserByUser(userID);
  const _variables: ListPollsByOwnerQueryVariables = {
    ...options,
    owner: admin.username,
    limit: options.limit || 1000,
    sortDirection: ModelSortDirection.DESC,
    // filter: {
    //     and: [
    //       { status: { ne: PollStatus.CLOSED } },
    //       { status: { ne: PollStatus.DELETED } },
    //     ],
    // },
  };
  if (nextToken != null) {
    _variables.nextToken = nextToken;
  }

  const variables = buildVariables<PollsByStatusQueryVariables>(_variables, [
    { key: "sortDirection", name: "ModelSortDirection" },
    { key: "filter", name: "ModelPollFilterInput" },
  ]);

  const q = query({
    operation: "result: listPollsByOwner",
    fields,
    variables,
  });
  return API.graphql(q);
}

export function getCustomConfiguration(companyID) {
  const _variables: ListCustomConfigurationByCompanyQueryVariables = {
    companyID,
  };
  const variables =
    buildVariables<ListCustomConfigurationByCompanyQueryVariables>(_variables, [
      { key: "companyID", name: "ID!" },
    ]);
  const q = query({
    operation: "result: listCustomConfigurationByCompany",
    fields: [{ items: ["id", "field", "value"] }],
    variables,
  });
  return API.graphql(q);
}

function getPollCount(username) {
  const _variables: GetPollCountQueryVariables = {
    id: username,
  };
  const variables = buildVariables<GetPollCountQueryVariables>(_variables, [
    { key: "id", name: "ID!" },
  ]);
  const q = query({
    operation: "result: getPollCount",
    fields: ["count"],
    variables,
  });
  return API.graphql(q);
}

export async function createCompanyPoll(
  fields: any[],
  pollInput: CreatePollInput,
  userId: string
) {
  const admin: any = await getAdminUserByUser(userId);
  pollInput.owner = admin.username;

  const pollCount: any = await getPollCount(admin.username);
  const customConfig: any = await getCustomConfiguration(admin.companyID);
  let packageSub: any = await getPackageByUser(admin.username);
  if (!packageSub) packageSub = await getPackageByTier(Tier.ENTERPRISE);

  let pollCap = customConfig.data.result.items.find(
    (i) => i.field === "pollCap"
  );
  if (pollCap?.value === undefined) {
    pollCap = {
      value: packageSub.noOfPolls,
    };
  }
  if (
    pollCap?.value !== undefined &&
    pollCount.data.result?.count > pollCap.value
  ) {
    throw new Error("Polls reached capacity");
  } else {
    return createIndividualPoll(fields, pollInput, admin.username);
  }
}

export async function createCompanyGroupPoll(
  mainInput: CreatePollInput,
  subInputs: CreateSubPollInput[],
  companyId: string,
  adminUsername: string
) {
  mainInput.owner = adminUsername;
  const pollCount: any = await getPollCount(adminUsername);
  const customConfig: any = await getCustomConfiguration(companyId);

  let packageSub: any = await getPackageByUser(adminUsername);
  if (!packageSub) packageSub = await getPackageByTier(Tier.ENTERPRISE);
  let pollCap = customConfig.data.result.items.find(
    (i) => i.field === "pollCap"
  );
  if (pollCap?.value === undefined) {
    pollCap = {
      value: packageSub.noOfPolls,
    };
  }

  console.log({ pollCap, pollCount });

  if (
    pollCap?.value !== undefined &&
    pollCount.data.result?.count > pollCap.value
  ) {
    throw new Error("Polls reached capacity");
  }

  return createGroupPoll(mainInput, subInputs, adminUsername);
}

import { getPackageByTier, getPackageByUser } from "./PackageManager";

export async function listSwingPollsByOwner(fields: any[], username: string) {
  const _variables: ListPollsByOwnerQueryVariables = {
    owner: username,
    sortDirection: ModelSortDirection.DESC,
    filter: { status: { eq: PollStatus.SWING } },
  };

  const variables = buildVariables<ListPollsByOwnerQueryVariables>(_variables, [
    { key: "filter", name: "ModelPollFilterInput" },
    { key: "sortDirection", name: "ModelSortDirection" },
    { key: "status", name: "PollStatus" },
  ]);

  var q = query({
    operation: "result: listPollsByOwner",
    fields,
    variables,
  });
  return API.graphql(q);
}

export async function createIndividualPoll(
  fields: any[],
  pollInput: CreatePollInput,
  username: string
) {
  // Package Subscription tier validation
  let packageSub: any;
  if (pollInput.isPremium) packageSub = await getPackageByTier(Tier.PREMIUM);
  else packageSub = await getPackageByUser(username);

  if (!packageSub && pollInput.companyID)
    packageSub = await getPackageByTier(Tier.ENTERPRISE);
  else if (!packageSub) packageSub = await getPackageByTier(Tier.FREE);

  if ((pollInput.options?.length || 0) > packageSub.pollOptionLimit) {
    if (pollInput.isPremium) {
      throw new Error(
        `Premium polls can only create up to ${packageSub.pollOptionLimit} options`
      );
    }
    throw new Error(
      `As a ${packageSub.tier.toLowerCase()} tier you can only create up to ${
        packageSub.pollOptionLimit
      } options`
    );
  }

  if (packageSub.pollDurationLimit == "24h") {
    const endDate = new Date(pollInput.endTime);
    const startDate = new Date(pollInput.startTime);
    const hours = differenceInHours(endDate, startDate);
    if (hours > 24) {
      throw new Error("Tier poll duration limit is 24h");
    }
  }
  const _variables: CreatePollMutationVariables = {
    input: {
      ...pollInput,
      status: PollStatus.CREATED,
    },
  };
  const q = createPollQuery(fields, _variables);
  return API.graphql(q);
}

export function createPollQuery(
  fields: any[],
  _variables: CreatePollMutationVariables
) {
  const variables = buildVariables<CreatePollMutationVariables>(_variables, [
    { key: "input", name: "CreatePollInput!" },
    { key: "condition", name: "ModelPollConditionInput" },
  ]);

  return mutation({
    operation: "result: createPoll",
    fields,
    variables,
  });
}

export function createSubPollQuery(
  fields: any[],
  _variables: CreateSubPollMutationVariables
) {
  const variables = buildVariables<CreateSubPollMutationVariables>(_variables, [
    { key: "input", name: "CreateSubPollInput!" },
    { key: "condition", name: "ModelSubPollConditionInput" },
  ]);

  return mutation({
    operation: "result: createSubPoll",
    fields,
    variables,
  });
}

function _createMainPoll(poll: CreatePollInput) {
  const _variables: CreatePollMutationVariables = {
    input: {
      ...poll,
      status: PollStatus.CREATED,
      isGroup: true,
    },
  };
  const fields = ["id", "owner"];
  const q = createPollQuery(fields, _variables);
  return API.graphql(q);
}

function _createSubPoll(poll: CreateSubPollInput, groupID: string) {
  const _variables: CreateSubPollMutationVariables = {
    input: {
      ...poll,

      status: PollStatus.CREATED,
      pollID: groupID,
    },
  };
  const fields = ["id"];
  const q = createSubPollQuery(fields, _variables);
  return API.graphql(q);
}

export function createPollComment(
  fields: any[],
  ideaCommentInput:CreatePollCommentInput
) {
  const _variables: CreatePollCommentMutationVariables = {
      input: {
        ...ideaCommentInput
      },
    };
  const variables = buildVariables<CreatePollCommentMutationVariables>(_variables, [
    { key: "input", name: "CreatePollCommentInput!" },
    { key: "condition", name: "ModelPollCommentConditionInput" },
  ]);
  return API.graphql( mutation({
      operation: "result: createPollComment",
      fields,
      variables,
    }));
}

export async function createSubPoll(
  mainPollID: string,
  subPollInput: CreateSubPollInput
) {

  return _createSubPoll(subPollInput, mainPollID);
}

export async function createGroupPoll(
  mainInput: CreatePollInput,
  subInputs: CreateSubPollInput[],
  username: string
) {
  let packageSub: any;
  if (mainInput.isPremium) packageSub = await getPackageByTier(Tier.PREMIUM);
  else packageSub = await getPackageByUser(username);

  if (!packageSub && mainInput.companyID)
    packageSub = await getPackageByTier(Tier.ENTERPRISE);
  else if (!packageSub) packageSub = await getPackageByTier(Tier.FREE);

  console.log("test", packageSub);

  if (!packageSub.groupPollAllowed) {
    throw new Error("Your tier doesnt support group poll");
  }

  // account for main poll
  if (subInputs.length > packageSub.groupPollLimit - 1) {
    throw new Error(
      `Your tier can only have ${packageSub.groupPollLimit} questions`
    );
  }

  [mainInput, ...subInputs].forEach((poll) => {
    if ((poll.options?.length || 0) > packageSub.pollOptionLimit) {
      if (mainInput.isPremium) {
        throw new Error(
          `Premium polls can only create up to ${packageSub.pollOptionLimit} options`
        );
      }
      throw new Error(
        `As a ${packageSub.tier.toLowerCase()} tier you can only create up to ${
          packageSub.pollOptionLimit
        } options`
      );
    }
  });

  if (packageSub.pollDurationLimit == "24h") {
    const endDate = new Date(mainInput.endTime);
    const startDate = new Date(mainInput.startTime);
    const hours = differenceInHours(endDate, startDate);
    if (hours > 24) {
      throw new Error("Tier poll duration limit is 24h");
    }
  }

  console.log("createGroupPoll", { mainInput });

  let mainPoll: any = await _createMainPoll(mainInput);
  await Promise.all(
    subInputs.map(async (poll) => {
      await _createSubPoll(poll, mainPoll.data?.result?.id);
    })
  );
  return { id: mainPoll.data?.result?.id };
}

export function listPollByGroupID(groupID: string) {}

export async function changeToPledge(fields: any[], pollID: string) {
  const _variable: UpdatePollMutationVariables = {
    input: {
      id: pollID,
      status: PollStatus.PLEDGING1,
    },
  };
  const q = updatePollQuery(fields, _variable);
  await API.graphql(q);
}

export async function listPollsByOwner(
  fields: any[],
  owner: string,
  filter?: ListPollsByOwnerQueryVariables["filter"],
  limit?: number,
  nextToken?: string | null
) {
  const _variables: ListPollsByOwnerQueryVariables = {
    owner,
    sortDirection: ModelSortDirection.DESC,
    limit: limit || 1000,
    filter,
  };
  if (nextToken != null) {
    _variables.nextToken = nextToken;
  }

  const variables = buildVariables<PollsByStatusQueryVariables>(_variables, [
    { key: "filter", name: "ModelPollFilterInput" },
    { key: "sortDirection", name: "ModelSortDirection" },
  ]);

  const q = query({
    operation: "result: listPollsByOwner",
    fields,
    variables,
  });
  return API.graphql(q);
}

export async function listPollsByStatus(
  fields: any[],
  status: PollStatus,
  limit,
  nextToken,
  publicMode,
  endTime?: Date
) {
  const _variables: PollsByStatusQueryVariables = {
    status: status,
    sortDirection: ModelSortDirection.DESC,
    filter: {
      startTime: { lt: new Date().toISOString() },
      endTime: { gt: (endTime || new Date()).toISOString() },
    },
    limit,
  };
  if (nextToken != null) {
    _variables.nextToken = nextToken;
  }

  const variables = buildVariables<PollsByStatusQueryVariables>(_variables, [
    { key: "filter", name: "ModelPollFilterInput" },
    { key: "sortDirection", name: "ModelSortDirection" },
    { key: "status", name: "PollStatus" },
  ]);

  var q = query({
    operation: "result: pollsByStatus",
    fields,
    variables,
  });
  if (publicMode == true) {
    q = Object.assign(q, { authMode: "AWS_IAM" });
  }
  return API.graphql(q);
}

function listSubPollsByMainIDQuery(fields: any[], pollID: string) {
  const _variables: ListSubPollsByMainPollQueryVariables = {
    pollID,
    filter: { status: { eq: PollStatus.CREATED } },
  };

  const variables = buildVariables<ListSubPollsByMainPollQueryVariables>(
    _variables,
    [{ key: "filter", name: "ModelSubPollFilterInput" }]
  );

  return query({
    operation: "result: listSubPollsByMainPoll",
    fields,
    variables,
  });
}

export async function createPollReport(input: CreatePollReportInput) {
  const _variables: CreatePollReportMutationVariables = {
    input,
  };

  const variables = buildVariables<CreatePollReportMutationVariables>(
    _variables,
    [{ key: "input", name: "CreatePollReportInput!" }]
  );

  const ops = mutation({
    operation: "result: createPollReport",
    fields: ["id", "title", "description"],
    variables,
  });

  return API.graphql(ops);
}

export async function listPollReportsByPoll(pollID: string) {
  const _variables: ListReportsByPollQueryVariables = {
    pollID,
  };

  const variables = buildVariables<ListReportsByPollQueryVariables>(
    _variables,
    [
      { key: "pollID", name: "ID!" },
      { key: "filter", name: "ModelPollReportFilterInput!" },
    ]
  );

  const fields = [
    {
      items: ["id", "title", "description", "owner", "createdAt", "updatedAt"],
    },
  ];
  const ops = query({
    operation: "result: listReportsByPoll",
    fields,
    variables,
  });

  return API.graphql(ops);
}

export async function getPollDetail(
  fields: any[],
  pollID: string,
  publicMode: boolean = false,
  password: string = ""
) {
  const _variables: GetPollQueryVariables = { id: pollID };
  let q = getPollByIDQuery(fields, _variables);
  if (publicMode === true) {
    q = Object.assign(q, { authMode: "AWS_IAM" });
  }
  const poll: any = await API.graphql(q);
  if (poll.data.result?.isGroup === true) {
    const subFields = [
      {
        items: [
          "id",
          "title",
          "question",
          "orderID",
          "description",
          "maxVote",
          "isNotExact",
          "owner",
          "createdAt",
          "image { key bucket region }",
          "video",
          "status",
          "private",
          {
            options: [
              "id",
              "title",
              "description",
              { detail: ["image { key bucket region }", "video"] },
            ],
            winOption: [
              "id",
              "title",
              "description",
              { detail: ["image { key bucket region }", "video"] },
            ],
          },
          { voteResults: [{ items: ["id", "count"] }] },
        ],
      },
    ];
    let qSub = listSubPollsByMainIDQuery(subFields, pollID);
    if (publicMode === true) {
      qSub = Object.assign(qSub, { authMode: "AWS_IAM" });
    }
    const subPolls: any = await API.graphql(qSub);
    poll.data.subPolls = subPolls;

    poll.data.subPolls?.data?.result?.items?.sort?.(
      (a: SubPoll, b: SubPoll) => {
        if (!b.orderID || !a.orderID) {
          if (!b.createdAt || !a.createdAt) return 0;
          return Date.parse(a.createdAt) - Date.parse(b.createdAt);
        }
        if (!b.createdAt || !a.createdAt) return 0;
        // OrderID low to high
        // Created at old to new
        return (
          a.orderID - b.orderID ||
          Date.parse(a.createdAt) - Date.parse(b.createdAt)
        );
      }
    );
  }
  return Promise.resolve(poll);
}
type listPublicPollsOptions = {
  filter?: ListPublicPollsQueryVariables["filter"];
  limit?: ListPublicPollsQueryVariables["limit"];
  nextToken?: ListPublicPollsQueryVariables["nextToken"];
};

export function listPublicPolls(
  fields: any[],
  options: listPublicPollsOptions,
  _private?: boolean | number | null,
  nextToken?: string
) {
  const _variables: ListPublicPollsQueryVariables = {
    limit: 100,
  };
  if (options.filter) _variables.filter = options.filter;
  if (options.limit) _variables.limit = options.limit;

  if (typeof _private === "boolean") {
    _variables.private = _private ? 1 : 0;
  } else {
    _variables.private = _private || 0;
  }

  if (nextToken !== undefined && nextToken !== null) {
    _variables.nextToken = nextToken;
  }

  const variables = buildVariables<ListPublicPollsQueryVariables>(_variables, [
    { key: "filter", name: "ModelPollFilterInput" },
    { key: "sortDirection", name: "ModelSortDirection" },
    { key: "status", name: "PollStatus" },
  ]);

  const q = query({
    operation: "result: listPublicPolls",
    fields,
    variables,
  });
  return API.graphql(q);
}

export async function editSubPoll(
  fields: any[],
  subPollID: string,
  _variables: UpdateSubPollMutationVariables
) {
  if (_variables.input) _variables.input.id = subPollID;
  const q = editSubPollByIDQuery(fields, _variables);
  return API.graphql(q);
}

export async function editPoll(
  fields: any[],
  pollID: string,
  _variables: UpdatePollMutationVariables,
  username?: string // undefined when swing vote
) {
  // Package Subscription tier validation
  if (username) {
    let packageSub: any;
    if (_variables.input?.isPremium)
      packageSub = await getPackageByTier(Tier.PREMIUM);
    else packageSub = await getPackageByUser(username);

    if (!packageSub && _variables.input?.companyID)
      packageSub = await getPackageByTier(Tier.ENTERPRISE);
    else if (!packageSub) packageSub = await getPackageByTier(Tier.FREE);

    if ((_variables.input?.options?.length || 0) > packageSub.pollOptionLimit) {
      if (_variables.input?.isPremium) {
        throw new Error(
          `Premium polls can only create up to ${packageSub.pollOptionLimit} options`
        );
      }
      throw new Error(
        `As a ${packageSub.tier.toLowerCase()} tier you can only create up to ${
          packageSub.pollOptionLimit
        } options`
      );
    }

    if (
      packageSub.pollDurationLimit == "24h" &&
      _variables.input?.endTime &&
      _variables.input?.startTime
    ) {
      const endDate = new Date(_variables.input?.endTime);
      const startDate = new Date(_variables.input?.startTime);
      const hours = differenceInHours(endDate, startDate);
      if (hours > 24) {
        throw new Error("Tier poll duration limit is 24h");
      }
    }
  }

  if (_variables.input) _variables.input.id = pollID;
  const q = editPollByIDQuery(fields, _variables);
  return API.graphql(q);
}

export function closePoll(pollID: string) {
  const _variables: UpdatePollMutationVariables = {
    input: {
      id: pollID,
      status: PollStatus.CLOSED,
    },
  };
  // _variables.input.id = pollID;
  _variables.input = { id: pollID };
  _variables.input.status = PollStatus.CLOSED;
  const q = editPollByIDQuery(["id"], _variables);
  return API.graphql(q);
}

export async function startPollPledge(
  fields: any[],
  pollID: string,
  _variables: UpdatePollMutationVariables
) {
  // _variables.input.id = pollID;
  _variables.input = { id: pollID };
  _variables.input.status = PollStatus.PLEDGING1;
  const q = editPollByIDQuery(fields, _variables);
  return API.graphql(q);
}

export function editSubPollByIDQuery(
  fields: any[],
  _variables: UpdateSubPollMutationVariables
) {
  const variables = buildVariables<UpdateSubPollMutationVariables>(_variables, [
    { key: "input", name: "UpdateSubPollInput!" },
    { key: "condition", name: "ModelSubPollConditionInput" },
  ]);

  return mutation({
    operation: "result: updateSubPoll",
    fields,
    variables,
  });
}

export function editPollByIDQuery(
  fields: any[],
  _variables: UpdatePollMutationVariables
) {
  const variables = buildVariables<UpdatePollMutationVariables>(_variables, [
    { key: "input", name: "UpdatePollInput!" },
    { key: "condition", name: "ModelPollConditionInput" },
  ]);

  return mutation({
    operation: "result: updatePoll",
    fields,
    variables,
  });
}

export function getPollByIDQuery(
  fields: any[],
  _variables: GetPollQueryVariables
) {
  const variables = buildVariables<GetPollQueryVariables>(_variables, [
    { key: "id", name: "ID!" },
  ]);

  return query({
    operation: "result: getPoll",
    fields,
    variables,
  });
}

export function updatePollQuery(
  fields: any[],
  _variables: UpdatePollMutationVariables
) {
  const variables = buildVariables<UpdatePollMutationVariables>(_variables, [
    { key: "input", name: "UpdatePollInput!" },
    { key: "condition", name: "ModelPollConditionInput" },
  ]);

  return mutation({
    operation: "result: updatePoll",
    fields,
    variables,
  });
}

export function deletePollQuery(
  fields: any[],
  _variables: DeletePollMutationVariables
) {
  const variables = buildVariables<DeletePollMutationVariables>(_variables, [
    { key: "input", name: "DeletePollInput!" },
    { key: "condition", name: "ModelPollConditionInput" },
  ]);

  return mutation({
    operation: "result: deletePoll",
    fields,
    variables,
  });
}

export async function canUserCreatePremiumPoll(
  userID: CanCreatePremiumPollInput["userID"]
) {
  const variables = buildVariables<CanCreatePremiumPollMutationVariables>(
    {
      input: {
        userID,
      },
    },
    [{ key: "input", name: "CanCreatePremiumPollInput!" }]
  );

  const res = (await API.graphql(
    mutation({
      operation: "result: canCreatePremiumPoll",
      variables,
    })
  )) as any;
  return res?.data?.result == "true";
}
function getPackageSubByUser(adminUsername: string): any {
  throw new Error("Function not implemented.");
}

export function endPoll(pollID: string) {
  const _variables: UpdatePollMutationVariables = {
    input: {
      id: pollID,
      endTime: new Date().toISOString()
    },
  };
  // _variables.input.id = pollID;
  _variables.input = { id: pollID };
  _variables.input.endTime = new Date().toISOString();
  const q = editPollByIDQuery(["id"], _variables);
  return API.graphql(q);
}
